import { buildApiUrl, fetchJSON, validateRequiredParams } from '../crm/crmApi';

export type InvestorOpportunityStage =
  | 'prospecting'
  | 'warm_intro'
  | 'first_meeting'
  | 'diligence'
  | 'term_sheet'
  | 'committed'
  | 'closed_won'
  | 'closed_lost';

export type InvestorType =
  | 'individual'
  | 'angel_group'
  | 'venture_capital'
  | 'private_equity'
  | 'family_office'
  | 'corporate_venture'
  | 'accelerator_incubator'
  | 'sovereign_wealth'
  | 'other';

export interface InvestorInput {
  organization_id: string;
  company_name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  opportunity_stage?: InvestorOpportunityStage;
  opportunity_summary?: string;
  notes?: string;
  investor_type?: InvestorType;
  firm_website?: string;
  fund_focus?: string;
  headquarters_location?: string;
  check_size_min?: number;
  check_size_max?: number;
  fund_size_notes?: string;
  institutional_investor?: boolean;
  institutional_investor_type?: string;
  institutional_investor_website?: string;
}

export interface Investor extends InvestorInput {
  id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  investor_type?: InvestorType;
  firm_website?: string;
  fund_focus?: string;
  headquarters_location?: string;
  check_size_min?: number;
  check_size_max?: number;
  fund_size_notes?: string;
  institutional_investor?: boolean;
  institutional_investor_type?: string;
  institutional_investor_website?: string;
}

export interface ListInvestorsParams {
  organization_id: string;
  search?: string;
  opportunity_stage?: InvestorOpportunityStage;
  investor_type?: InvestorType;
  limit?: number;
  offset?: number;
}

const investorsService = {
  async list(params: ListInvestorsParams): Promise<{ data: Investor[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'investorCrm.investorsService.list'
    });
    const url = buildApiUrl('/api/investor-crm/investors', params as any);
    return fetchJSON(url, {
      description: 'List investor CRM investors',
      context: {
        organization_id: params.organization_id,
        search: params.search,
        opportunity_stage: params.opportunity_stage,
        investor_type: params.investor_type
      }
    });
  },
  async listByStage(organization_id: string, stage: InvestorOpportunityStage, limit = 200): Promise<Investor[]> {
    const { data } = await this.list({ organization_id, opportunity_stage: stage, limit, offset: 0 });
    return data || [];
  },
  async get(id: string, organization_id: string): Promise<{ data: Investor | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'investorCrm.investorsService.get'
    });
    const url = buildApiUrl(`/api/investor-crm/investors/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get investor CRM investor',
      context: { id, organization_id }
    });
  },
  async create(payload: InvestorInput): Promise<{ data: Investor }>{
    validateRequiredParams(payload, ['organization_id', 'company_name'], {
      service: 'investorCrm.investorsService.create'
    });
    const url = buildApiUrl('/api/investor-crm/investors');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create investor CRM investor',
      context: { organization_id: payload.organization_id, company_name: payload.company_name }
    });
  },
  async update(id: string, payload: Partial<InvestorInput> & { organization_id: string }): Promise<{ data: Investor }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'investorCrm.investorsService.update'
    });
    const url = buildApiUrl(`/api/investor-crm/investors/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update investor CRM investor',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async updateStage(id: string, organization_id: string, opportunity_stage: InvestorOpportunityStage): Promise<{ data: Investor }>{
    return this.update(id, { organization_id, opportunity_stage });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'investorCrm.investorsService.remove'
    });
    const url = buildApiUrl(`/api/investor-crm/investors/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete investor CRM investor',
      context: { id, organization_id }
    });
  }
};

export default investorsService;
